import { Trans } from 'react-i18next';

import { actions, isOwner } from 'entities';

import { Alert, Button, Column, IconButton, Modal, Row } from 'components/Blocks';
import {
  CircleDollarIcon,
  CircleHelpIcon,
  ClockIcon,
  SigmaIcon,
  WorkflowIcon,
} from 'components/Icons';

import { useLang, useUser } from 'hooks';
import { useProjectProfile } from 'pages/Projects/ProjectProfile/context';

interface CPIHelpModalProps {
  children?: never;
  cpi: string;
}

export function CPIHelpModal({ cpi }: CPIHelpModalProps) {
  const user = useUser();
  const { t } = useLang();

  const { project } = useProjectProfile();

  const syncSample = actions.projects.syncSampleCPI.use({
    params: {
      projectId: project.id,
    },
  });

  return (
    <>
      <Modal
        preventOpenAutoFocus
        width="min(90vw, 580px)"
        title={
          <Row gap="2">
            <WorkflowIcon />
            <h2 className="text-lg font-semibold leading-none tracking-tight">Project CPI</h2>
          </Row>
        }
        trigger={
          <IconButton size="small">
            <CircleHelpIcon size={14} />
          </IconButton>
        }
      >
        <p className="mt-2">
          <span className="font-semibold text-primary">{cpi}</span>{' '}
          <Trans
            i18nKey="cpi-is-explanation"
            components={{ strong: <strong className="font-semibold italic" /> }}
          />
        </p>

        <Row className="mx-auto w-fit rounded-md border p-2 text-sm">
          <span className="text-xl font-medium italic">CPI</span>

          <span>=</span>

          <Column gap="1">
            <Row gap="2">
              <CircleDollarIcon />
              <span>{t('project-total')}</span>
            </Row>

            <hr />

            <Row gap="2">
              <SigmaIcon />
              <span>{t('completes')}</span>
            </Row>
          </Column>
        </Row>

        <p className="text-sm">
          {t('sample-automatically-updated')}{' '}
          {isOwner(project, user) && (
            <span>
              <Trans
                i18nKey="sample-out-of-sync"
                components={{
                  Button: (
                    <Button
                      variant="link"
                      {...syncSample.buttonProps}
                      tooltipContent={
                        <Row gap="2">
                          <ClockIcon size={14} />
                          {t('may-take-5min')}
                        </Row>
                      }
                    >
                      .
                    </Button>
                  ),
                }}
              />
            </span>
          )}
        </p>

        <Alert level="warning">
          <Trans
            i18nKey="cpi-automated-warning"
            components={{ strong: <strong className="font-semibold italic" /> }}
          />
        </Alert>
      </Modal>
    </>
  );
}
